import { v4 as uuid } from "uuid";

import { _ } from "js/vendor";
import { HeaderPositionType, TrayType, ContentBlockType, AssetType, NodeType, PositionType, ListStyleType, TextStyleType } from "common/constants";
import { ShowConfirmationDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import getLogger from "js/core/logger";

const logger = getLogger();

export default async function convertSlideToTemplate(canvas, templateId) {
    const template = _.find(canvas.slideTemplates, { id: templateId });
    if (!template) {
        return;
    }

    let maxPrimaryItemsCount = null;
    let currentPrimaryItemsCount = null;

    const originalModel = _.cloneDeep(canvas.model);
    const restoreOriginalModel = async () => {
        try {
            canvas.model = originalModel;
            canvas.updateTemplate(canvas.model.template_id);
            await canvas.refreshCanvas({ transition: false, forceRender: true });
            await canvas.saveCanvasModel();
        } catch (err) {
            logger.error(err, "convertSlideToTemplate() failed to update template", { slideId: canvas.dataModel?.id, templateId });
        }
    };

    PresentationEditorController.hideCanvasControls();
    canvas.showSpinner(null, true);

    try {
        const slideTemplate = new template();

        const layout = canvas.model.layout;

        if (layout.headerPosition && layout.headerPosition != HeaderPositionType.NONE && !slideTemplate.availableHeaderPositions.contains(layout.headerPosition)) {
            if (slideTemplate.allowHeader) {
                layout.headerPosition = slideTemplate.availableHeaderPositions[0];
            } else {
                layout.headerPosition = HeaderPositionType.NONE;
            }
        }

        if (layout.trayLayout && layout.trayLayout != TrayType.NONE && !slideTemplate.availableTrayLayouts.contains(layout.trayLayout)) {
            if (slideTemplate.availableTrayLayouts.length) {
                layout.trayLayout = slideTemplate.availableTrayLayouts[0];
            } else {
                layout.trayLayout = TrayType.NONE;
            }
        }

        const postProcessingFunction = ConvertTemplateModel(canvas, templateId);
        canvas.model.elements = _.merge({}, slideTemplate.defaultData, canvas.model.elements);

        canvas.model.template_id = templateId;
        canvas.updateTemplate(templateId);
        canvas.layouter.clear();

        try {
            await canvas.refreshCanvas({ transition: false });
        } catch (err) {
            // check if we have too many items in the model for the new template
            const primaryElement = canvas.layouter.elements.primary;
            if (primaryElement.isInstanceOf("CollectionElement") && primaryElement.model.items.length > primaryElement.maxItemCount) {
                maxPrimaryItemsCount = primaryElement.maxItemCount;
                currentPrimaryItemsCount = primaryElement.model.items.length;

                primaryElement.model.items = primaryElement.model.items.slice(0, maxPrimaryItemsCount);

                await canvas.refreshCanvas({ transition: false });
            } else {
                throw (err);
            }
        }

        if (postProcessingFunction) {
            await postProcessingFunction(canvas);
            await canvas.refreshCanvas({ transition: false });
        }

        await canvas.saveCanvasModel();

        if (maxPrimaryItemsCount && currentPrimaryItemsCount && currentPrimaryItemsCount > maxPrimaryItemsCount) {
            if (!await ShowConfirmationDialog({
                title: "Too many items for this type of slide",
                message: `The slide you are switching to only allows ${maxPrimaryItemsCount} items but your current slide has ${currentPrimaryItemsCount} items. If you continue to switching to this slide, the extra items will be removed.`,
                okButtonLabel: "Switch Slide",
            })) {
                await restoreOriginalModel();
            }
        }
    } catch (err) {
        logger.error(err, "convertSlideToTemplate() failed to convert slide template", { slideId: canvas.dataModel?.id, templateId });

        await restoreOriginalModel();

        if (err.message == "Layout doesn't fit") {
            ShowErrorDialog({
                title: "Oops, we can't fit your content into this slide!",
                message: "Beautiful.ai will to convert your existing content to the new slide but sometimes if there is just too much text or items on your slide, we won't be able to convert it. You can try reducing the number of items or amount of text before trying again."
            });
        } else {
            ShowErrorDialog({
                title: "Oops, something went wrong!",
                message: "Unfortunately we weren't able to convert your slide to this new smart slide template."
            });
        }
    }

    PresentationEditorController.showCanvasControls();
    canvas.hideSpinner();
}

function ConvertTemplateModel(canvas, toTemplateId) {
    const model = canvas.model;
    const fromTemplateId = canvas.slideTemplate.constructor.id;

    let postProcessingFunction;

    // convert to a neutral model
    let currentModel = model.elements.primary;
    let items = [];

    let currentItems = currentModel.items || [];
    let useNestedItems = false;

    // pre-process
    switch (fromTemplateId) {
        case "kanban":
        case "horizontal-task-list":
            if (toTemplateId.equalsAnyOf("kanban", "horizontal-task-list", "textlist")) {
                useNestedItems = true;
                model.elements.primary.items = currentItems;
            } else {
                currentItems = _.map(currentItems, i => i.items).flat();
            }
            break;
        case "textlist":
            if (toTemplateId.equalsAnyOf("kanban", "horizontal-task-list")) {
                useNestedItems = true;
                for (let col = 0; col <= _.max(currentItems.map(i => i.col)); col++) {
                    items.push({
                        label: currentModel[`columnHeader${col}`],
                        items: currentItems.filter(i => i.col == col)
                    });
                }
                model.elements.primary.items = items;
            }
            break;
        case "headline2":
            let textBlocks = model.elements.primary.text.blocks.filter(b => !(b.type.equalsAnyOf(ContentBlockType.ICON, ContentBlockType.DIVIDER, ContentBlockType.BULLET_POINT)));
            if (textBlocks.length > 0) {
                model.elements.header = { text: { blocks: [textBlocks[0]] } };
                currentItems = [];
                if (textBlocks.length > 1) {
                    currentItems = [{ text: { blocks: textBlocks.slice(1) } }];
                }
            }
            break;
    }

    if (currentItems && !useNestedItems) {
        // convert each item from the current model to a neutral model
        for (let currentItem of currentItems) {
            let item = _.clone(currentItem);

            // strip these model properties
            item.textAlign = null;

            switch (fromTemplateId) {
                case "user_testimonial":
                    item.title = currentItem.quote;
                    item.body = currentItem.title;
                    break;
                case "arrow_bars":
                case "gantt_chart":
                    item.title = currentItem.label;
                    delete item.label;
                    break;
                case "collage":
                    item.color = currentItem.cellColor;
                    if (currentItem.blocks) {
                        let iconBlock = currentItem.blocks.find(block => block.type == "icon");
                        if (iconBlock) {
                            Object.assign(item, getContentProps(iconBlock));
                        }
                        let textBlocks = currentItem.blocks.filter(block => block.type != "icon" && block.type != "divider");
                        if (textBlocks.length) {
                            item.title = textBlocks[0].content;
                        }
                        if (textBlocks.length > 1) {
                            item.body = textBlocks[1].content;
                        }
                    }
                    break;
                case "target":
                    if (currentItem.segments[0].labelType == "inside") {
                        item.title = { text: currentItem.segments[0].label };
                    } else {
                        let connection = currentModel.annotations.connections.items.find(c => c.target == `/primary/${currentItem.id}/segment0`);
                        if (connection) {
                            let node = currentModel.annotations.items.find(item => item.id == connection.source);
                            if (node) {
                                item.title = node.title;
                                item.body = node.body;
                            }
                        }
                    }
                    break;
            }

            items.push(item);
        }
        model.elements.primary = { items };
    }

    switch (toTemplateId) {
        case "textlist":
            if (useNestedItems) {
                model.elements.primary.showColumnHeaders = true;

                model.elements.primary.items = _.cloneDeep(currentItems).map((currentItem, idx) => {
                    model.elements.primary[`columnHeader${idx}`] = currentItem.label;

                    const columnModel = currentItem;
                    const allBlocks = currentItem.items.reduce((blocks, item) => {
                        if (!item.text?.blocks?.length) {
                            return blocks;
                        }

                        const itemBlocks = item.text.blocks.map((block, idx) => {
                            if (idx === 0) {
                                return {
                                    ...block,
                                    listStyle: ListStyleType.BULLET,
                                    textStyle: TextStyleType.BULLET_LIST,
                                    indent: 0
                                };
                            }

                            return { ...block, indent: 1 };
                        });

                        return [...blocks, ...itemBlocks];
                    }, []);

                    if (!columnModel.text) {
                        columnModel.text = { blocks: [] };
                    }
                    columnModel.text.blocks = allBlocks.map(block => ({ ...block, id: uuid() }));

                    return columnModel;
                });
            } else {
                model.elements.primary.showColumnHeaders = false;

                const allBlocks = [];
                _.cloneDeep(currentItems).forEach(currentItem => {
                    if (!currentItem.text?.blocks?.length) {
                        return;
                    }

                    allBlocks.push(...currentItem.text.blocks.map((block, idx) => ({
                        ...block,
                        textStyle: TextStyleType.BULLET_LIST,
                        listStyle: idx === 0 ? ListStyleType.BULLET : ListStyleType.TEXT,
                        indent: idx === 0 ? 0 : 1
                    })));
                });

                model.elements.primary.items = [];
                if (allBlocks.length) {
                    model.elements.primary.items.push({
                        text: { blocks: allBlocks.map(block => ({ ...block, id: uuid() })) }
                    });
                }
            }
            break;
        case "chart_column":
        case "chart_bar":
        case "chart_line":
        case "chart_area":
        case "chart_pie":
            model.elements.primary = {
                items: []
            };

            // check each prior item for graphs
            const convert = [...items];
            for (let i = 0; i < convert.length; i++) {
                let item = convert[i];

                // if it appears to be a graph, attempt to convert it
                if (canvas.chartUtils.isChartModel(item)) {
                    // check if it can be converted
                    const currentType = canvas.chartUtils.getChartType(item);
                    const convertTo = toTemplateId.replace("chart_", "");
                    if (convertTo !== currentType) {
                        item = canvas.chartUtils.convertChartType(item, convertTo);
                    }

                    // save the chart
                    model.elements.primary.items.push(item);
                }
            }

            break;
        case "chart_waterfall":
            model.elements.primary = {
                items: []
            };
            break;

        case "big-numbers":
            model.elements.primary.items = (model.elements.primary.items || [])
                .splice(0, canvas.BigNumbers.maximumItems) // maximum supported items
                .map((el, i) => {
                    const row = Math.floor(i / 2);
                    return { ...el, row };
                });

            break;

        case "table-v2":

            if (fromTemplateId.startsWith("chart_")) {
                let chartData = currentModel.items[0].chart.chartData;
                let cols = [{
                    index: 0,
                    styles: "headerCol",
                    size: 100
                }];
                for (let col = 0; col < chartData.series[0].data.length; col++) {
                    cols.push({
                        index: col + 1,
                        style: "defaultCol",
                        size: 50
                    });
                }

                let rows = [{
                    index: 0,
                    styles: "headerRow",
                    size: 40
                }];

                let cells = [];

                for (let cat of chartData.xAxis.categories) {
                    cells.push({
                        row: 0,
                        col: chartData.xAxis.categories.indexOf(cat) + 1,
                        format: "text",
                        cellText: { text: cat }
                    });
                }

                for (let row = 0; row < chartData.series.length; row++) {
                    rows.push({
                        index: row + 1,
                        style: "defaultRow",
                        size: 30
                    });
                    cells.push({
                        row: row + 1,
                        col: 0,
                        format: "text",
                        cellText: { text: chartData.series[row].name }
                    });
                    for (let col = 0; col < chartData.series[row].data.length; col++) {
                        cells.push({
                            row: row + 1,
                            col: col + 1,
                            format: "number",
                            cellText: { text: chartData.series[row].data[col].y.toString() }
                        });
                    }
                }

                model.elements.primary.cols = cols;
                model.elements.primary.rows = rows;
                model.elements.primary.cells = cells;
                model.elements.primary.items = null;
            }
            break;
        case "arrow_bars":
        case "radial_bar_chart":
            for (let item of items) {
                item.label = item.title;
                item.value = Math.clamp(item.value ?? 50, 0, 100);
            }
            break;
        case "timeline":
            model.elements.primary.showContent = items.some(item => item.content_type);
            postProcessingFunction = async canvas => {
                canvas.getCanvasElement().elements.primary.distributeMilestones();
            };
            break;
        case "agenda":
            model.elements.primary = {
                showIndex: true,
                items: items.map(item => ({ ...item, type: "item" }))
            };

            break;
        case "collage":
            model.elements.primary.showGutter = true;
            model.elements.primary.fullBleed = true;
            for (let item of items) {
                item.blocks = [];

                // verify there's enough info to convert
                if (item.content_type && item.content_value) {
                    if (!(item.title || item.body) || item.content_type == AssetType.IMAGE || item.content_type == AssetType.VIDEO || item.content_type == AssetType.STOCK_VIDEO) {
                        // use asset as cell background
                    } else {
                        // use asset as a block
                        item.blocks.push({
                            type: "icon",
                            ...getContentProps(item),
                        });
                        item.content_type = null;
                        item.content_value = null;
                    }
                } else {
                    // if not enough info to convert, make sure to delete it
                    delete item.content_type;
                    delete item.content_value;
                }

                if (item.title) {
                    item.blocks.push({
                        type: "title",
                        content: item.title
                    });
                }
                if (item.body) {
                    item.blocks.push({
                        type: "body",
                        content: item.body
                    });
                }
            }
            break;
        case "wordCloud":
            model.elements.primary = {};
            break;
        case "user_testimonial":
            for (let item of items) {
                item.quote = item.title;
                item.title = item.body;
                item.body = null;
                item.content = getContentProps(item);
            }
            break;
        case "orgchart":
            const orgchart = createOrgChart(model.elements.primary.items);
            model.elements.primary = {
                defaultNodeStyle: "box",
                hideRootNode: false,
                layout: "expanded",
                layoutDirection: "vertical",
                items: orgchart
            };
            break;
        case "funnel":
            model.elements.primary.baseSize = 15;
            model.elements.primary.capSize = 65;
            model.elements.primary.inverted = false;
            break;
        case "swot_diagram":
            model.elements.primary.items = _.take(items, 4);
            break;
        case "gantt_chart":
            model.elements.primary.showImage = items.some(item => item.content_type);
            for (let item of items) {
                item.startDate = Date.UTC(new Date().getFullYear(), 0, items.indexOf(item) * 7 + 1);
                item.endDate = Date.UTC(new Date().getFullYear(), 0, items.indexOf(item) * 7 + 31);
                item.label = item.title;
            }
            break;
        case "kanban":
        case "horizontal-task-list":
            if (!useNestedItems) {
                model.elements.primary.items = [{
                    items
                }];
            }
            break;
        case "step_circular":
        case "hub_and_spoke":
            for (let item of items) {
                item.nodeType = items.some(item => item.content_type) ? NodeType.CONTENT_AND_TEXT : NodeType.BULLET_TEXT;
                item.frameType = null;
                item.decorationStyle = null;
                item.size = null;
            }
            break;
        case "flow_chart":
            let x = 0;
            let y = 0;
            model.elements.primary.connectors = { items: [] };
            let isNewRow = false;
            for (let item of items) {
                item.x = x;
                item.y = y;
                item.nodeType = item.content_type ? NodeType.CONTENT_AND_TEXT : NodeType.BOX;
                item.textDirection = PositionType.BOTTOM;
                item.textWidth = item.content_type ? 300 : 150;
                x += 0.25;
                if (x >= 0.8) {
                    x = 0;
                    y += 0.5;
                }

                if (items.indexOf(item) > 0) {
                    model.elements.primary.connectors.items.push({
                        connectorType: "step",
                        startAnchor: "anchor-right",
                        endAnchor: isNewRow ? "anchor-top" : "anchor-free",
                        endDecoration: "arrow",
                        source: items[items.indexOf(item) - 1].id,
                        target: item.id
                    });
                }

                isNewRow = x == 0;
            }

            postProcessingFunction = async canvas => {
                canvas.getCanvasElement().elements.primary.centerLayout();
            };
            break;
        case "target":
            model.layout.slideColor = "colorful";
            for (let item of items) {
                if (item.title) {
                    item.segments = [{
                        color: "auto",
                        label: item.title.text,
                        labelType: "inside"
                    }];
                }
            }
            break;
        case "calendar-month":
            model.elements.primary.calendarType = "month";
            break;
        case "calendar-week":
            model.elements.primary.calendarType = "week";
            break;
        case "textgrid_photos":
            for (let item of items) {
                if (item.frameType == "theme" || item.frameType == "none") {
                    item.frameType = "rect";
                }
            }
            break;
        case "headline2":
            model.elements.primary.blocks = [];
            if (model.elements.header?.label) {
                model.elements.primary.blocks.push({
                    type: ContentBlockType.CAPTION,
                    content: model.elements.header.label
                });
            }
            if (model.elements.header?.title) {
                model.elements.primary.blocks.push({
                    type: ContentBlockType.HEADING,
                    content: model.elements.header.title
                });
            }
            if (model.elements.header?.description) {
                model.elements.primary.blocks.push({
                    type: ContentBlockType.BODY,
                    content: model.elements.header.description
                });
            }
            break;
        case "xygraph":
            for (let item of items) {
                item.x = 0.1 + Math.random() * .65;
                item.y = 0.1 + Math.random() * .75;
                item.size = 100;

                if (item.content_type && item.content_value) {
                    item.nodeType = NodeType.CONTENT_AND_TEXT;
                }
            }
            break;
    }

    return postProcessingFunction;
}

function createOrgChart(items) {
    let template = '[{"id":"0","index":0,"nodeType":"person","title":{"text":""},"body":{"text":""},"titleTextStyle":"title"},{"id":"1","index":0,"nodeType":"person","parent":"0","title":{"text":""},"body":{"text":""},"titleTextStyle":"title"},{"id":"2","index":1,"nodeType":"person","parent":"0","title":{"text":""},"body":{"text":""},"titleTextStyle":"title"},{"id":"3","index":0,"nodeType":"person","parent":"2","title":{"text":""},"body":{"text":""},"titleTextStyle":"title"},{"id":"4","index":1,"nodeType":"person","parent":"2","title":{"text":""},"body":{"text":""},"titleTextStyle":"title"},{"id":"10541651276907653","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"1","index":0,"titleTextStyle":"title"},{"id":"10661651276908959","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"1","index":1,"titleTextStyle":"title"},{"id":"10841651276912882","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"10541651276907653","index":0,"titleTextStyle":"title"},{"id":"11001651276914597","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"10661651276908959","index":0,"titleTextStyle":"title"},{"id":"11161651276915894","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"3","index":0,"titleTextStyle":"title"},{"id":"11341651276917569","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"4","index":0,"titleTextStyle":"title"},{"id":"11521651276920100","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"10841651276912882","index":0,"titleTextStyle":"title"},{"id":"11681651276921523","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"11001651276914597","index":0,"titleTextStyle":"title"},{"id":"11841651276922788","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"11161651276915894","index":0,"titleTextStyle":"title"},{"id":"12001651276924361","title":{"text":""},"body":{"text":""},"style":"text-box","parent":"11341651276917569","index":0,"titleTextStyle":"title"}]';

    let data = JSON.parse(template);

    for (let i = 0; i < items.length; i++) {
        let node = data[i];
        if (node) {
            let item = items[i];
            item.id = node.id;
            item.index = node.index;
            item.nodeType = "person";
            item.parent = node.parent;
        }
    }

    items = _.take(items, data.length);

    return items;
}

function getContentProps(item) {
    return {
        content_type: item.content_type,
        content_value: item.content_value,
        aoiTop: item.aoiTop,
        aoiLeft: item.aoiLeft,
        aoiBottom: item.aoiBottom,
        aoiRight: item.aoiRight
    };
}
